<template>
  <div id="documents-container">
    <div id="title-container">
      <h2>{{ $t('projectDetails.documents.documents') }}</h2>
      <div id="suppliers-counter">{{ pageOptions.rows ?? 0 }}</div>
    </div>

    <table-wrapper
      v-model:filters="filters"
      v-model:page-options="pageOptions"
      v-model:selected="selectedFiles"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="files"
      :loading="isLoading"
      :mode="TableMode.LAZY"
      :row-class="getRowClass"
      row-clickable
      :selection="true"
      :table-style="{ 'table-layout': 'fixed', 'word-break': 'break-all' }"
      @row-click="goToDocumentDetailView"
    >
      <template #cell(hasRendition)="{ data }">
        <font-awesome-icon v-if="data?.hasRendition" :icon="faCheck" />
        <font-awesome-icon v-else :icon="faXmark" />
      </template>
      <template #cell(referenceNumber)="{ data }">
        {{ data?.referenceNumber }}
        <template v-if="data?.previousReferenceNumbers?.length">
          <div v-tooltip.top="data.previousReferenceNumbers.join(', ')">
            {{ $t('filesOverview.plusOthers') }}
          </div>
        </template>
      </template>
    </table-wrapper>

    <div v-if="selectedFiles.length" id="inspection-buttons">
      <vf-button
        v-if="
          isUserRole(inspectionAllowedRoles) &&
          isInspectionForSelectedDocumentsAllowed()
        "
        id="reject-button"
        :size="buttonSizes.large"
        :variant="buttonVariants.tertiary"
        @click="handleStatusChangeConfirmation(InspectorStatusEnum.rejected)"
      >
        {{ $t('projectDetails.documents.rejectDocuments') }}
      </vf-button>

      <vf-button
        v-if="
          isUserRole(inspectionAllowedRoles) &&
          isInspectionForSelectedDocumentsAllowed()
        "
        :size="buttonSizes.large"
        :variant="buttonVariants.primary"
        @click="handleStatusChangeConfirmation(InspectorStatusEnum.accepted)"
      >
        {{ $t('projectDetails.documents.acceptDocuments') }}
      </vf-button>
      <vf-button
        v-if="isUserRole(changeInspectionDueDateAllowedRoles)"
        id="change-inspection-due-date-button"
        :size="buttonSizes.large"
        :variant="buttonVariants.primary"
        @click="dueDateModalRef?.show()"
      >
        {{ $t('filesOverview.changeInspectionDueDate') }}
      </vf-button>
    </div>
    <inspector-confirmation-modal
      ref="confirmationModalRef"
      :status-changes="statusChanges"
      :is-changing-inspector-status="isChangingInspectorStatus"
      :results="inspectorStatusChangeResults"
      @inspector-confirmation="addInspectorStatus"
      @hide="clearInspectorStatusChangeResults"
    />
    <change-inspection-due-date-modal
      ref="dueDateModalRef"
      :file-ids="getIdsOfSelectedFiles()"
      :project-id="projectId"
      @inspection-due-dates-updated="handleInspectionDueDatesUpdated"
    />
  </div>
</template>
<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';
  import { useToast } from '@/composables/useToast';
  import axios from 'axios';
  import ChangeInspectionDueDateModal from '@/components/ChangeInspectionDueDateModal/ChangeInspectionDueDateModal.vue';
  import { formatDateForDisplay } from '@/helpers';
  import {
    RowClass,
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import endpointsList from '../../helpers/endpointsList';
  import {
    IFilesQueryRequest,
    IFileTableData,
    IInspectorStatusAssignmentResponseDto,
    InspectorStatusEnum,
    IPaginatedFileTableDataResponse,
    ISetMultipleInspectorStatusesDto,
    Role,
    Serialized,
    SupplierDocumentStatus,
    ProjectType,
    Order,
  } from 'types';
  import { InspectorStatusHelper } from 'utils';
  import { useRoles } from '@/composables/useRoles';
  import {
    PROJECT_ID_PARAM,
    PROJECT_TYPE_PARAM,
    Route,
    routeParams,
    UPLOAD_REFERENCE_NUMBER_PARAM,
  } from '@/routes';
  import { TABLE_NAMESPACE } from './constants';
  import { IStatusChanges } from './FilesOverviewTable.types';
  import InspectorConfirmationModal from '../InspectorConfirmationModal/InspectorConfirmationModal.vue';
  import { faFileCad } from '@fortawesome/pro-solid-svg-icons';
  import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

  const props = defineProps<{ projectId: string }>();
  const { t } = useI18n();
  const router = useRouter();
  const { createErrorToast } = useToast();
  const { isUserRole } = useRoles();

  const {
    filters,
    filterParams,
    pageOptions,
    isLoading,
    selection: selectedFiles,
    fetch,
  } = useTableWrapper<IFileTableData, IFilesQueryRequest>(
    TableMode.LAZY,
    'uploadDate',
    getFiles,
    Order.DESC,
    true,
    TABLE_NAMESPACE,
  );

  const files = ref<IFileTableData[]>([]);
  const statusChanges = ref<IStatusChanges | Record<string, never>>({});
  const isChangingInspectorStatus = ref(false);
  const inspectionAllowedRoles = ref([Role.Admin, Role.Inspector]);
  const changeInspectionDueDateAllowedRoles = ref([
    Role.Admin,
    Role.Inspector,
    Role.DocuManager,
  ]);
  const inspectorStatusChangeResults = ref<
    IInspectorStatusAssignmentResponseDto | undefined
  >(undefined);

  const tableFields = computed(() => {
    return new TableFieldsBuilder<IFileTableData>({
      sortable: true,
      labelTranslationKey: 'projectDetails.documents',
    })
      .addField('documentId', {
        label: '#',
        width: '9%',
      })
      .addField('hasRendition', {
        width: '5%',
        icon: faFileCad,
        sortable: false,
      })
      .addField('referenceNumber', { width: '9%' })
      .addField('revision', { width: '9%' })
      .addField('supplierName', {
        isVisible: isUserRole([Role.Admin, Role.DocuManager, Role.Inspector]),
        width: '14%',
      })
      .addField('contentType', { width: '11%' })
      .addField('uploadDate', {
        width: '11%',
        valueGetter: (d) => formatDateForDisplay(d.uploadDate),
      })
      .addField('inspectionDueDate', {
        width: '9%',
        valueGetter: (d) => formatDateForDisplay(d.inspectionDueDate),
      })
      .addField('coworker', { width: '11%' })
      .addField('documentProcessingStatus', {
        sortable: false,
        width: '12%',
        valueTranslationKey: 'documentProcessingStatus',
      })
      .build();
  });

  const filterConfiguration = computed(() => {
    return new TableFiltersBuilder<IFileTableData, IFilesQueryRequest>({
      translationKey: 'projectDetails.documents',
    })
      .addInputFilter('documentId', {
        width: '130px',
        simpleLabel: true,
      })
      .addInputFilter('referenceNumber', {
        width: '120px',
        simpleLabel: true,
      })
      .addInputFilter('revision', {
        width: '70px',
        simpleLabel: true,
      })
      .addInputFilter('supplierName', {
        width: '180px',
        simpleLabel: true,
      })
      .addSelectFilter('contentType', {
        width: '140px',
        simpleLabel: true,
        // TODO: get select options from API
        selectOptions: ['AssetDocumentation'],
        translationKey: undefined,
      })
      .addInputFilter('coworker', {
        width: '120px',
        simpleLabel: true,
      })
      .addSelectFilter('documentProcessingStatus', {
        width: '100px',
        simpleLabel: true,
        selectOptions: Object.values(SupplierDocumentStatus),
        translationKey: 'documentProcessingStatus',
      })
      .build();
  });

  async function getFiles() {
    try {
      const response = await axios.get<IPaginatedFileTableDataResponse>(
        endpointsList.documentsUploads.getFilesForProject(
          props.projectId,
          pageOptions.value.page,
          pageOptions.value.perPage,
          pageOptions.value.sortBy,
          pageOptions.value.sortOrder === Order.DESC ? -1 : 1,
          filterParams.value,
        ),
      );
      const { files: fetchedFiles, resultsCount } = response.data;
      files.value = fetchedFiles;
      pageOptions.value.rows = resultsCount;
    } catch {
      createErrorToast(
        t('projectDetails.documents.errors.couldNotLoadDocuments'),
      );
    }
  }

  function goToDocumentDetailView(data: IFileTableData) {
    router.push({
      name: Route.DOCUMENT_DETAIL_VIEW,
      params: {
        [routeParams[Route.DOCUMENT_DETAIL_VIEW][PROJECT_TYPE_PARAM]]:
          ProjectType.SUPPLIER_PROJECT,
        [routeParams[Route.DOCUMENT_DETAIL_VIEW].documentOid]:
          data._id.toString(),
      },
      query: {
        [routeParams[Route.DOCUMENT_DETAIL_VIEW].query[PROJECT_ID_PARAM]]:
          props.projectId,
        [routeParams[Route.DOCUMENT_DETAIL_VIEW].query[
          UPLOAD_REFERENCE_NUMBER_PARAM
        ]]: data.referenceNumber,
      },
    });
  }

  const confirmationModalRef = ref<InstanceType<
    typeof InspectorConfirmationModal
  > | null>(null);
  function handleStatusChangeConfirmation(
    statusChangeType: InspectorStatusEnum,
  ) {
    statusChanges.value = {
      statusChangeType,
      files: selectedFiles.value.map((file) => ({
        _id: file._id.toString(),
        documentId: file.documentId.toString(),
        uploadId: file.uploadId.toString(),
        supplierId: file.supplierId.toString(),
      })),
    };
    confirmationModalRef.value?.show();
  }

  function isInspectionForSelectedDocumentsAllowed(): boolean {
    return selectedFiles.value.every((file) => file.canSetInspectorStatus);
  }
  async function addInspectorStatus() {
    try {
      isChangingInspectorStatus.value = true;
      const requestBody: Serialized<ISetMultipleInspectorStatusesDto> = {
        fileIds: statusChanges.value.files.map((file) => file._id),
        inspectorStatus: statusChanges.value.statusChangeType,
      };
      const response = await axios.patch<IInspectorStatusAssignmentResponseDto>(
        endpointsList.documentsUploads.assignInspectorStatusToMultipleFiles,
        requestBody,
      );
      inspectorStatusChangeResults.value = response.data;
    } catch {
      createErrorToast(t('errors.error'));
    } finally {
      isChangingInspectorStatus.value = false;
      statusChanges.value = {};
      selectedFiles.value = [];
      fetch(false);
    }
  }
  function clearInspectorStatusChangeResults(): void {
    inspectorStatusChangeResults.value = undefined;
  }

  function getIdsOfSelectedFiles(): string[] {
    return selectedFiles.value.map((file) => file._id.toString());
  }

  const dueDateModalRef = ref<InstanceType<
    typeof ChangeInspectionDueDateModal
  > | null>(null);
  function handleInspectionDueDatesUpdated() {
    dueDateModalRef.value?.hide();
    selectedFiles.value = [];
    fetch(false);
  }

  function getRowClass(file: IFileTableData): string {
    return file.didSharePointUploadFail ? RowClass.DANGER : '';
  }
</script>

<style scoped lang="scss">
  #documents-container {
    margin-top: $tab-view-margin-top;

    #title-container {
      display: flex;
      align-items: center;
      margin-bottom: 20px;

      h2 {
        @include subheading;
      }

      #suppliers-counter {
        align-self: flex-start;
        @include heading-counter;
        margin-left: 10px;
      }
    }

    #select-all-button-container {
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }

    div#inspection-buttons {
      margin-top: 50px;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      justify-items: center;

      #change-inspection-due-date-button {
        grid-column: 3/4;
      }
    }
  }
</style>
